import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Checkbox,
  Box,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import { debounce } from 'lodash';
import { searchProcedureCodesByType } from '../../services/procedureCodesService';

/**
 * SymptomsDropdown - Optimized SNOMED symptom selector
 * 
 * Features:
 * - Debounced search (only searches when user types 2+ characters)
 * - No initial data load (prevents slow page loads)
 * - Multiple symptom selection with checkboxes
 * - SNOMED code display
 */
const SymptomsDropdown = React.memo(({ 
  selectedSymptoms = [], 
  onChange, 
  label = "Select Symptoms",
  placeholder = "Search symptoms...",
  disabled = false 
}) => {
  const [searchOptions, setSearchOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Use ref to track selected symptoms to avoid stale closures in debounced function
  const selectedRef = useRef(selectedSymptoms);
  useEffect(() => {
    selectedRef.current = selectedSymptoms;
  }, [selectedSymptoms]);

  // Debounced search function for SNOMED codes
  const debouncedSearch = useMemo(
    () => debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setSearchOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const codes = await searchProcedureCodesByType('SNOMED', searchTerm, true);
        
        // Format for dropdown and filter out already selected
        const selectedValues = selectedRef.current;
        const formattedCodes = codes
          .map(code => ({
            id: code.id,
            code: code.procedure_code || code.code || '',
            label: code.name || code.description || code.procedure_code || '',
            description: code.name || code.description || '',
            value: code.name || code.description || code.procedure_code || ''
          }))
          .filter(code => code.label && code.value)
          .filter(code => !selectedValues.includes(code.value))
          .slice(0, 30); // Limit results for performance
        
        setSearchOptions(formattedCodes);
      } catch (err) {
        console.error('Error searching SNOMED codes:', err);
        setSearchOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [] // Empty deps - uses ref for selectedSymptoms
  );

  // Trigger search when input changes
  useEffect(() => {
    debouncedSearch(inputValue);
  }, [inputValue, debouncedSearch]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handle selection change
  const handleSelectionChange = useCallback((event, newValue) => {
    const symptomNames = newValue.map(item => item.value);
    onChange(symptomNames);
  }, [onChange]);

  // Convert selected symptom strings to option objects
  const selectedOptions = useMemo(() => {
    return selectedSymptoms.map(symptom => {
      const found = searchOptions.find(code => code.value === symptom);
      return found || { value: symptom, label: symptom, code: '', description: symptom };
    });
  }, [selectedSymptoms, searchOptions]);

  // Render option with checkbox
  const renderOption = useCallback((props, option, { selected }) => {
    const { key, ...otherProps } = props;
    return (
      <li key={key} {...otherProps}>
        <Checkbox
          checked={selected}
          sx={{ marginRight: 1, p: 0.5 }}
          size="small"
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {option.label}
          </Typography>
          {option.code && (
            <Typography variant="caption" color="text.secondary">
              SNOMED: {option.code}
            </Typography>
          )}
        </Box>
      </li>
    );
  }, []);

  // Render selected tags
  const renderTags = useCallback((value, getTagProps) => {
    return value.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      return (
        <Chip
          key={key}
          label={option.label || option.value}
          {...tagProps}
          size="small"
          sx={{ borderRadius: '8px' }}
        />
      );
    });
  }, []);

  return (
    <Autocomplete
      multiple
      options={searchOptions}
      value={selectedOptions}
      onChange={handleSelectionChange}
      inputValue={inputValue}
      onInputChange={(event, value, reason) => {
        if (reason === 'input') {
          setInputValue(value);
        } else if (reason === 'clear') {
          setInputValue('');
        }
      }}
      getOptionLabel={(option) => option.label || option.value || ''}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      disabled={disabled}
      disableCloseOnSelect
      loading={loading}
      loadingText="Searching symptoms..."
      noOptionsText={
        inputValue.length < 2 
          ? "Type 2+ characters to search symptoms..." 
          : "No symptoms found"
      }
      renderOption={renderOption}
      renderTags={renderTags}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedOptions.length === 0 ? placeholder : ""}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiInputLabel-root': {
              fontFamily: 'Roboto, Arial, sans-serif',
              fontWeight: 500,
              fontSize: '0.875rem',
            },
            '& .MuiInputBase-input': {
              fontFamily: 'Roboto, Arial, sans-serif',
              fontWeight: 500,
              fontSize: '0.875rem',
            },
            '& .MuiInputBase-input::placeholder': {
              fontFamily: 'Roboto, Arial, sans-serif',
              fontWeight: 400,
              fontSize: '0.875rem',
              opacity: 0.7,
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      ChipProps={{
        size: 'small',
        variant: 'outlined'
      }}
      sx={{
        '& .MuiAutocomplete-listbox': {
          maxHeight: 300
        }
      }}
    />
  );
});

SymptomsDropdown.displayName = 'SymptomsDropdown';

export default SymptomsDropdown;
